import * as React from 'react';
import { Box, Card, Chip, Stack, Typography } from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import NumberFormat from 'react-number-format';

// Compact summary of premiums auto-added from announcement promotions.
// Groups chips by SOURCE PROMO so the user can see exactly which promo gives
// which gift — and so it's obvious which chips will disappear when they uncheck
// a promotion.
//
// Props:
//   - Premiumus: array of premium items (with __fromAnnouncement marker)
//   - SelectedAnnouncements: [{ announcementNo, ... }] currently checked
//   - EligibleAnnouncements: full detect payload [{ announcementNo, header, packs: [{premiums: [...]}] }]
export default function AutoPremiumsSummary({ Premiumus, SelectedAnnouncements, EligibleAnnouncements }) {
    const autoItems = (Premiumus || []).filter((p) => p.__fromAnnouncement);
    if (autoItems.length === 0) return null;

    const priceMap = autoItems.reduce((acc, it) => {
        acc[it.premium_id] = Number(it.salePrice || 0);
        return acc;
    }, {});
    const nameMap = autoItems.reduce((acc, it) => {
        acc[it.premium_id] = it.premiumName || it.premium_id;
        return acc;
    }, {});

    // Build per-announcement groups from the ENABLED packs only (packsMatched),
    // so a pack the user toggled off also drops its premiums from this summary.
    const headerMap = {};
    (EligibleAnnouncements || []).forEach((a) => {
        headerMap[a.announcementNo] = a.header || a.announcementNo;
    });
    const groups = (SelectedAnnouncements || [])
        .map((sel) => {
            const need = {};
            (sel.packsMatched || []).forEach((pack) => {
                (pack.premiums || []).forEach((pr) => {
                    if (!pr.premium_id) return;
                    const qty = Number(pr.qty || 0);
                    if (qty <= 0) return;
                    if (!need[pr.premium_id]) {
                        need[pr.premium_id] = {
                            premium_id: pr.premium_id,
                            name: nameMap[pr.premium_id] || pr.premiumName || pr.premium_id,
                            qty: 0,
                        };
                    }
                    need[pr.premium_id].qty += qty;
                });
            });
            return {
                announcementNo: sel.announcementNo,
                header: headerMap[sel.announcementNo] || sel.announcementNo,
                items: Object.values(need),
            };
        })
        .filter((g) => g.items.length > 0);

    if (groups.length === 0) return null;

    const totalValue = groups.reduce(
        (sum, g) => sum + g.items.reduce((s, it) => s + it.qty * (priceMap[it.premium_id] || 0), 0),
        0,
    );
    const totalItems = groups.reduce((sum, g) => sum + g.items.length, 0);

    return (
        <Card variant="outlined" sx={{ p: 1.25, borderColor: '#d4a017', bgcolor: '#fffaeb' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <RedeemIcon sx={{ color: '#d4a017', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7c5300' }}>
                    ຂອງແຖມອັດຕະໂນມັດ / Auto Premiums
                </Typography>
                <Chip
                    size="small"
                    label={`${totalItems} item${totalItems > 1 ? 's' : ''}`}
                    sx={{ height: 20, bgcolor: '#d4a017', color: '#fff', fontWeight: 700 }}
                />
                <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }} alignItems="baseline">
                    <Typography variant="caption" color="textSecondary">value:</Typography>
                    <Typography variant="subtitle2" sx={{ color: '#7c5300', fontWeight: 700 }}>
                        <NumberFormat
                            thousandsGroupStyle="thousand"
                            value={totalValue}
                            prefix="₭"
                            decimalSeparator="."
                            displayType="text"
                            thousandSeparator
                        />
                    </Typography>
                </Stack>
            </Stack>

            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                {groups.map((g) => (
                    <Box key={g.announcementNo}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>
                            <CardGiftcardIcon sx={{ fontSize: 14, color: '#7c5300' }} />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={g.announcementNo}
                                sx={{ height: 16, fontSize: '0.6rem', borderColor: '#d4a017', color: '#7c5300' }}
                            />
                            <Typography variant="caption" sx={{ color: '#7c5300', fontWeight: 600 }}>
                                {g.header}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5, pl: 2.25 }}>
                            {g.items.map((it) => (
                                <Chip
                                    key={`${g.announcementNo}-${it.premium_id}`}
                                    size="small"
                                    icon={<RedeemIcon sx={{ fontSize: 14 }} />}
                                    label={
                                        <span>
                                            {(it.name || it.premium_id).substring(0, 30)}
                                            <strong style={{ marginLeft: 6 }}>× {it.qty}</strong>
                                        </span>
                                    }
                                    sx={{
                                        bgcolor: '#fff',
                                        border: '1px solid #e4c97a',
                                        color: '#7c5300',
                                        '& .MuiChip-icon': { color: '#d4a017' },
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Card>
    );
}
